import Service from '@ember/service';
import userflow from 'userflow.js';
import config from 'uplisting-frontend/config/environment';
import type { ModelRegistry } from 'ember-data/types/registries/model';

export default class UserflowService extends Service {
  #initialized = false;

  // One-time SDK init only, mirroring crisp.configure — the per-transition
  // identify lives in the application route's identifyUser hook so both
  // integrations refresh through the same canonical path.
  public configure(): void {
    if (this.#initialized) {
      return;
    }

    if (!config.userflow.token) {
      return;
    }

    userflow.init(config.userflow.token);
    this.#initialized = true;
  }

  public identify(user: ModelRegistry['user']): void {
    if (!this.#initialized || !user) {
      return;
    }

    // signature enables Userflow Identity Verification: it's the HMAC of the
    // user id signed with USERFLOW_SECRET on the API (user.userFlowHash). Once
    // verification is enabled Userflow rejects identify calls without it.
    void userflow.identify(
      user.id,
      {
        name: user.name,
        email: user.email,
        signed_up_at: user.createdAt.toISOString(),
      },
      { signature: user.userFlowHash },
    );
  }

  public start(contentId: string, options?: { once?: boolean }): void {
    if (!this.#initialized || !contentId) {
      return;
    }

    void userflow.start(contentId, options);
  }

  public reset(): void {
    this.#clearStorage();

    if (!this.#initialized) {
      return;
    }

    userflow.reset();
  }

  // userflow.reset() is queued onto the async SDK loader, and we navigate away
  // immediately on logout (window.open(_self)), so the userflow* identity keys
  // the SDK owns in localStorage can survive the redirect and rebind the next
  // user on a shared browser — the same race crisp guards against, and one that
  // matters here given Support's user-impersonation flows. Wipe them
  // synchronously so a prior identity never leaks across sessions.
  #clearStorage(): void {
    for (const storage of [window.localStorage, window.sessionStorage]) {
      const keysToRemove: string[] = [];

      for (let i = 0; i < storage.length; i++) {
        const key = storage.key(i);

        if (key?.toLowerCase().includes('userflow')) {
          keysToRemove.push(key);
        }
      }

      keysToRemove.forEach((key) => storage.removeItem(key));
    }
  }
}

declare module '@ember/service' {
  interface Registry {
    userflow: UserflowService;
  }
}
